import dedent from 'ts-dedent'
import {
  Provider,
  QueryAndOutputs,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  WorkflowComponentCodeParams
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class MultiColFormula extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.multicolformula',
        group: 'Data Preparation',
        title: 'Multi-col formula',
        description: `
                This component computes new values based on a given expression and a set of fields to apply the expression to.
                Use $a to refer to the value of the current column.
                `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'expression',
          type: WorkflowComponentParamType.StringSql,
          title: 'Expression',
          placeholder: '$a + 1',
          allowExpressions: false
        },
        {
          name: 'columns',
          type: WorkflowComponentParamType.Column,
          title: 'Column to apply expression to',
          mode: WorkflowComponentParameterMode.Multiple,
          parent: 'source'
        },
        {
          name: 'mode',
          type: WorkflowComponentParamType.Selection,
          title: 'mode',
          options: ['Create new columns', 'Replace values in existing columns'],
          default: 'Create new columns'
        },
        {
          name: 'prefix',
          type: WorkflowComponentParamType.String,
          title: 'prefix',
          default: 'new',
          optional: true,
          showIf: [
            {
              parameter: 'mode',
              value: 'Create new columns'
            }
          ]
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)
    let query = ''
    const expression = preparedParams.expression as string

    switch (connection.type) {
      case Provider.BigQuery:
      case Provider.Snowflake: {
        const except = connection.type === Provider.Snowflake ? 'EXCLUDE' : 'EXCEPT'
        let select = ''
        if (preparedParams.mode === 'Create new columns') {
          const newcols = (preparedParams.columns as Array<string>)
            .map((x) => `${expression.replace(/\$a/g, x)} AS ${`${params.prefix}_${x}`}`)
            .join(',\n')
          select = dedent`
                  SELECT
                      *,
                      ${dedent(newcols)}
                  FROM ${connection.quoteName(preparedParams.source as string)}`
        } else {
          const newcols = (preparedParams.columns as Array<string>)
            .map((x) => `${expression.replace(/\$a/g, x)} AS ${x}`)
            .join(',\n')
          select = dedent`
                  SELECT
                      * ${except} (${(preparedParams.columns as Array<string>).join(', ')}),
                      ${dedent(newcols)}
                  FROM ${connection.quoteName(preparedParams.source as string)}`
        }
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.DatabricksRest: {
        let select = ''
        if (preparedParams.mode === 'Create new columns') {
          const newcols = (params.columns as Array<string>)
            .map((x) => `${expression.replace(/\$a/g, x)} AS ${`${params.prefix}_${x}`}`)
            .join(',\n')
          select = dedent`
                  SELECT /*+ REPARTITION(40) */
                      *,
                      ${dedent(newcols)}
                  FROM ${connection.quoteName(preparedParams.source as string)}`
        } else {
          const newcols = (params.columns as Array<string>)
            .map((x) => `${expression.replace(/\$a/g, x)} AS ${x}`)
            .join(',\n')
          select = dedent`
                  SELECT /*+ REPARTITION(40) */
                      * EXCEPT (${(params.columns as Array<string>).join(', ')}),
                      ${dedent(newcols)}
                  FROM ${connection.quoteName(preparedParams.source as string)}`
        }
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.Redshift:
      case Provider.Postgres:
      default:
        {
          let select = ''
          if (preparedParams.mode === 'Create new columns') {
            const newcols = (preparedParams.columns as Array<string>)
              .map((x) => `${expression.replace(/\$a/g, x)} AS ${`${params.prefix}_${x}`}`)
              .join(',\n')
            select = dedent`
                SELECT
                    *,
                    ${dedent(newcols)}
                FROM ${connection.quoteName(preparedParams.source as string)}`
            query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
          } else {
            const newcols = (preparedParams.columns as Array<string>)
              .map((x) => `${expression.replace(/\$a/g, x)} AS ${`${params.prefix}_${x}`}`)
              .join(',\n')
            select = dedent`
                SELECT
                    *,
                    ${dedent(newcols)}
                FROM ${connection.quoteName(preparedParams.source as string)}`
            query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
            query = dedent`
                        ${query}
                        ${(preparedParams.columns as Array<string>)
                          .map((x) => `ALTER TABLE ${outputTablename} DROP COLUMN ${x};`)
                          .join('\n')}
                        ${(preparedParams.columns as Array<string>)
                          .map(
                            (x) => `ALTER TABLE ${outputTablename} RENAME COLUMN ${`${params.prefix}_${x}`} TO ${x};`
                          )
                          .join('\n')}`
          }
        }
        break
    }

    return {
      query,
      outputs: { result: outputTablename }
    }
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Postgres, Provider.Redshift, Provider.DatabricksRest]
  }
}

export { MultiColFormula }
